import { useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useProjetos } from "@/features/projetos/hooks/useProjetos";
import { STATUS_PROJETO } from "@/features/projetos/types";

/** Concluído (3) e Cancelado (4) não recebem apontamento. */
const STATUS_ENCERRADOS = new Set([3, 4]);

interface ProjetoSearchComboboxProps {
  /** Id do projeto (0/undefined = nenhum). */
  value?: number;
  onChange: (id: number) => void;
  /** Esconde projetos concluídos/cancelados (o selecionado continua aparecendo). */
  somenteAtivos?: boolean;
  /** Texto do "nenhum" (ex.: "Todos os projetos" num filtro). */
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Projeto com busca por nome ou cliente. A lista de projetos já vem inteira do servidor
 * (é curta) e é filtrada aqui; o id interno nunca aparece.
 */
export function ProjetoSearchCombobox({
  value,
  onChange,
  somenteAtivos = false,
  placeholder = "Selecione o projeto...",
  disabled,
  className,
}: ProjetoSearchComboboxProps) {
  const [open, setOpen] = useState(false);
  const { data: projetos = [], isLoading } = useProjetos();

  const selecionado = value ? projetos.find((p) => p.id === value) : undefined;
  const opcoes = projetos.filter((p) => !somenteAtivos || !STATUS_ENCERRADOS.has(p.status) || p.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className={cn("relative w-full", className)}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
            disabled={disabled}
            type="button"
          >
            <span className={cn("truncate", !selecionado && "text-muted-foreground")}>
              {selecionado ? selecionado.nome : value && isLoading ? "Carregando…" : placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        {!!value && !disabled && (
          <button
            type="button"
            aria-label="Limpar projeto"
            className="absolute right-8 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground"
            onClick={(e) => { e.stopPropagation(); onChange(0); }}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <PopoverContent className="w-[380px] max-w-[calc(100vw-2rem)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar projeto ou cliente..." />
          <CommandList>
            <CommandEmpty>Nenhum projeto encontrado.</CommandEmpty>
            <CommandGroup>
              {opcoes.map((p) => (
                <CommandItem
                  key={p.id}
                  value={`${p.nome} ${p.clienteNome ?? ""} ${p.id}`}
                  onSelect={() => { onChange(p.id); setOpen(false); }}
                >
                  <Check className={cn("mr-2 h-4 w-4 shrink-0", value === p.id ? "opacity-100" : "opacity-0")} />
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate">{p.nome}</span>
                    <span className="truncate text-xs text-muted-foreground">
                      {[p.clienteNome, STATUS_PROJETO[p.status]].filter(Boolean).join(" · ")}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
